/**
 * React wrapper around YOM's `<yom-report>` web component.
 *
 * - Loads the component script lazily (only pages that render a report pay for it).
 * - Feeds tokens from the page's EmbedTokenManager: `tokenProvider` for the
 *   first load and the 401 retry, and a pushed `el.token` whenever the manager
 *   refreshes before expiry, so a long-open tab never shows an expired-token error.
 * - Draws our own skeleton in the report's final footprint until `yom-load`,
 *   and our own error card with Retry on `yom-error` (no CLS, no YOM-styled errors).
 * - Themes through the admin tokens (index.css) and mirrors light/dark.
 */
import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { loadYomEmbed } from '@/lib/yom/embedLoader';
import type { EmbedTokenManager } from '@/lib/yom/tokenManager';
import type { EmbedSource, YomReportName } from '@/lib/yom/types';
import type { DateRange } from '@/lib/yom/range';
import { cn } from '@/lib/utils';
import { ReportSkeleton, RESERVE_CLASS, type SkeletonVariant } from './ReportSkeleton';

interface YomReportElement extends HTMLElement {
  token: string | null;
  tokenProvider?: () => Promise<string>;
  refresh(): void;
}

export interface YomReportProps {
  manager: EmbedTokenManager;
  /** Which token in the bundle to use ('site', 'app', 'sites'). */
  source: string;
  report: YomReportName;
  title?: string;
  range?: DateRange;
  metric?: string;
  interval?: string;
  dim?: string;
  event?: string;
  limit?: number;
  funnel?: string;
  minutes?: number;
  kpis?: string[];
  compare?: boolean;
  currency?: string;
  /** Seconds between auto refreshes (realtime defaults to 30 inside YOM). */
  refresh?: number;
  skeleton: SkeletonVariant;
  className?: string;
  /** Optional sub-tenant (platform rollups keyed by project). */
  subTenant?: string;
}

type Phase = 'loading' | 'ready' | 'error';

export function YomReport(props: YomReportProps) {
  const { manager, source, report, title, skeleton, className } = props;
  const { isDark } = useTheme();
  const ref = useRef<YomReportElement | null>(null);
  const [src, setSrc] = useState<EmbedSource | null>(() => manager.peek()?.sources[source] ?? null);
  const [scriptReady, setScriptReady] = useState(
    () => typeof window !== 'undefined' && Boolean(window.customElements?.get('yom-report'))
  );
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState<string | null>(null);
  // Errors after a successful load come from background refreshes; YOM keeps
  // the last good data on screen for those, so we do too.
  const awaitingForeground = useRef(true);

  // Loads the script and the token bundle. Leaves `phase` alone on success:
  // the element's own yom-load decides when the report is ready.
  const boot = useCallback(() => {
    loadYomEmbed()
      .then(() => setScriptReady(true))
      .catch(() => {
        setPhase('error');
        setMessage('The report could not load. Check your connection and try again.');
      });
    manager
      .getBundle()
      .then((b) => {
        const s = b.sources[source];
        if (s) setSrc(s);
        else {
          setPhase('error');
          setMessage('This report is not available yet.');
        }
      })
      .catch(() => {
        setPhase('error');
        setMessage('We could not reach your analytics. Try again in a moment.');
      });
  }, [manager, source]);

  useEffect(() => {
    boot();
  }, [boot]);

  // Wire the element from a callback ref. It runs in the same commit that
  // connects the element, before the microtask in which `<yom-report>` starts
  // its first fetch, and again whenever React hands us a different node, so
  // the provider and listeners are always on the element that will fire.
  const managerRef = useRef(manager);
  const sourceRef = useRef(source);
  managerRef.current = manager;
  sourceRef.current = source;
  const unwireRef = useRef<(() => void) | null>(null);
  const setNode = useCallback((node: YomReportElement | null) => {
    unwireRef.current?.();
    unwireRef.current = null;
    ref.current = node;
    if (!node) return;
    node.tokenProvider = () =>
      managerRef.current.getToken(sourceRef.current, { rejected: node.token });
    const onLoad = () => {
      awaitingForeground.current = false;
      setPhase('ready');
      setMessage(null);
    };
    const onError = (e: Event) => {
      if (!awaitingForeground.current) return;
      const detail = (e as CustomEvent<{ status?: number }>).detail;
      setPhase('error');
      setMessage(
        detail?.status === 403
          ? 'This report is not switched on for your site yet.'
          : 'We could not load this report right now.'
      );
    };
    node.addEventListener('yom-load', onLoad);
    node.addEventListener('yom-error', onError);
    unwireRef.current = () => {
      node.removeEventListener('yom-load', onLoad);
      node.removeEventListener('yom-error', onError);
    };
  }, []);

  // Push tokens the manager refreshed before expiry (no 401 round trip).
  useEffect(
    () =>
      manager.subscribe((b) => {
        const el = ref.current;
        const fresh = b.sources[source]?.token;
        if (el && fresh && fresh !== el.token) el.token = fresh;
      }),
    [manager, source]
  );

  // Any data-shaping attribute change is a foreground load.
  const fetchKey = [
    report,
    props.range?.from,
    props.range?.to,
    props.metric,
    props.interval,
    props.dim,
    props.event,
    props.limit,
    props.funnel,
    props.minutes,
    props.subTenant,
  ].join('|');
  useEffect(() => {
    awaitingForeground.current = true;
  }, [fetchKey]);

  const retry = () => {
    awaitingForeground.current = true;
    setPhase('loading');
    setMessage(null);
    if (!scriptReady || !src) return boot();
    ref.current?.refresh();
  };

  const attrs: Record<string, string | undefined> = {
    project: src?.project,
    api: src?.api,
    report,
    title,
    from: props.range?.from,
    to: props.range?.to,
    metric: props.metric,
    interval: props.interval,
    dim: props.dim,
    event: props.event,
    limit: props.limit?.toString(),
    funnel: props.funnel,
    minutes: props.minutes?.toString(),
    kpis: props.kpis?.join(','),
    compare: props.compare === undefined ? undefined : String(props.compare),
    currency: props.currency,
    refresh: props.refresh?.toString(),
    'sub-tenant': props.subTenant,
    theme: isDark ? 'dark' : 'light',
  };

  return (
    <div
      className={cn('relative flex flex-col', RESERVE_CLASS[skeleton], className)}
      aria-busy={phase === 'loading'}
    >
      {scriptReady &&
        src &&
        createElement('yom-report', {
          ref: setNode,
          ...attrs,
          style: { visibility: phase === 'ready' ? 'visible' : 'hidden' },
        })}
      {phase === 'loading' && (
        <ReportSkeleton variant={skeleton} title={title} className="absolute inset-0" />
      )}
      {phase === 'error' && (
        <div className="bg-card absolute inset-0 flex flex-col rounded-xl border p-5 shadow">
          {title && <div className="text-base font-semibold">{title}</div>}
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <AlertCircle className="text-muted-foreground h-6 w-6" aria-hidden />
            <p className="text-muted-foreground max-w-xs text-sm">{message}</p>
            <Button variant="outline" size="sm" onClick={retry}>
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
